<template>
  <div class="vital-check">
    <div
      v-if="!isNpcCheck"
      class="vital-select-input-group el-input-group el-input-group--append el-input-group--prepend"
    >
      <div class="el-input-group__prepend">Is Position</div>

      <state-input
        :state="positionState"
        :initial-value="state.check.position"
        :show-label="false"
        width="160px"
        :disabled="readonly"
        class="position-input"
        @input="value => updatePosition(value)"
      />
    </div>
    <div v-if="isNpcCheck">
      <character-select
        :selected="state.check.npcKey"
        :characters="getNpcCharacters(emitter.caseConf)"
        :disabled="readonly"
        :errors="sourceCharacterErrors"
        placeholder="Select VIP"
        @input="updateNpc"
      />
    </div>
    <div
      class="vital-select-input-group el-input-group el-input-group--append el-input-group--prepend"
    >
      <div v-if="!isNpcCheck" class="el-input-group__prepend">And Is Behavior</div>
      <div v-else class="el-input-group__prepend">Is Behavior</div>

      <state-input
        :state="animationStateWithEligibleValues"
        :initial-value="state.check.value"
        :synced-value="state.check.value"
        :show-label="false"
        width="240px"
        :disabled="readonly"
        class="behaviour-input"
        @input="value => updateBehaviour(value)"
      >
        <template slot="append">?</template>
      </state-input>
    </div>
  </div>
</template>

<script>
import keyBy from 'lodash/keyBy';

import StateInput from '~lib/common/components/StateInput.vue';
import CharacterSelect from './CharacterSelect.vue';
import nodeMixins from './mixins/NodeMixins';

import states from '~lib/common/config/states';
import { getNpcCharacters } from '~lib/common/utils/characters';

export default {
  name: 'behaviour-check',
  components: {
    StateInput,
    CharacterSelect,
  },
  mixins: [ nodeMixins ],
  props: ['animationId', 'readonly', 'emitter', 'ikey', 'getData', 'putData', 'control'],
  data() {
    return {
      state: {
        check: {
          value: null,
          position: 'ANY',
          npcKey: undefined
        },
      },
    };
  },
  computed: {
    behaviourState() {
      return states.find(s => s.id === this.animationId);
    },
    behaviourValuesByKey() {
      return keyBy(this.behaviourState.possibleValues, 'key');
    },
    eligibleAnimationValues() {
      if (!this.behaviourState) {
        return [];
      }
      let { possibleValues } = this.behaviourState;
      if (this.state.check.position !== 'ANY') {
        possibleValues = possibleValues.filter(
        value =>
          value.meta &&
          value.meta.compatiblePositions &&
          value.meta.compatiblePositions.includes(this.state.check.position)
        );
      }
      possibleValues = possibleValues.filter(
        value =>
          value.meta &&
          (!value.meta.isMilitaryOnly || this.emitter.isMilitaryOrg || this.isMilitaryAnimation)
      );
      if (this.isNpcCheck) {
        return possibleValues;
      } else {
        return [{
          key: 'ANY',
          label: { en: 'Any Behavior', es: 'Any Behavior' },
        }].concat(possibleValues);
      }
    },
    animationStateWithEligibleValues() {
      return {
        ...this.behaviourState,
        possibleValues: this.eligibleAnimationValues,
      };
    },
    isRepeating() {
      const selectedBehaviour = this.behaviourValuesByKey[this.state.check.value];
      if (!selectedBehaviour) {
        return true;
      }
      return selectedBehaviour.meta && selectedBehaviour.meta.isRepeating;
    },
    positionState() {
      const positionState = states.find(s => s.id === 'position');
      return {
        ...positionState,
        possibleValues: [
          {
            key: 'ANY',
            label: { en: 'Any Position', es: 'Any Position' },
          },
        ].concat(positionState.possibleValues),
      };
    },
    isMilitaryAnimation() {
      const selectedBehaviour = this.behaviourValuesByKey[this.state.check.value];
      if (!selectedBehaviour) {
        return false;
      }
      return selectedBehaviour.meta && selectedBehaviour.meta.isMilitaryOnly;
    },
    isNpcCheck() {
      return this.animationId === 'npcAnimation';
    },
    sourceCharacterErrors() {
      return this.getErrorsById('missingSourceCharacter');
    },
  },
  created() {
    this.state.check = { ...this.state.check, ...this.getData(this.ikey) };
    if (!this.state.check.value) {
      if (this.isNpcCheck) {
        this.updateBehaviour('IDLE');
      } else {
        this.updateBehaviour('ANY');
      }
    }
    this.emitter.trigger('process', this.control);
  },
  methods: {
    updatePosition(position) {
      this.state.check.position = position;
      const eligibleAnimationEnumValues = this.eligibleAnimationValues.map(
        enumValue => enumValue.key,
      );
      if (!eligibleAnimationEnumValues.includes(this.state.check.value)) {
        this.updateBehaviour('ANY');
      }
      this.update();
    },
    updateBehaviour(behaviour) {
      if (!behaviour) {
        return;
      }
      this.state.check.value = behaviour;
      if (this.isNpcCheck) {
        this.state.check.type = this.isRepeating ? 'NPC_ANIMATION_REPEATING' : 'NPC_ANIMATION';
      } else {
        this.state.check.type = this.isRepeating ? 'ANIMATION_REPEATING' : 'ANIMATION';
      }
      this.update();
    },
    updateNpc(npcKey) {
      this.state.check.npcKey = npcKey;
      this.update();
    },
    update() {
      this.putData(this.ikey, {
        ...this.state.check,
        id: 'anim',
        ids: [this.state.check.position],
        operator: 'EQUAL',
      });
      this.emitter.trigger('process', this.control);
      this.$forceUpdate();
    },
    getNpcCharacters,
  },
};
</script>

<style scoped>
.vital-check {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.vital-select {
  width: 130px;
}

.vital-select-input-group {
  width: auto;
}

.operator-select {
  width: 70px;
}
</style>
